/**
 * Noticing that a newer APK exists, and installing it — the app's own side of
 * the thing a store would otherwise do.
 *
 * Everywhere except inside the Android shell this file is inert. The web build
 * updates itself through the service worker (appUpdate.ts) and the iOS one comes
 * from TestFlight, so both already have somebody watching; an APK handed out
 * over a link has nobody. `hasNativeUpdate` is the switch, and it is false
 * unless the plugin is really registered — a bundle newer than the shell around
 * it must find that out rather than call into nothing.
 *
 * `android/app/src/main/java/com/makaek/chat/UpdatePlugin.kt` is the other end,
 * kept in step by hand.
 */
import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';

/** The plugin's name on both sides — `name` in the @CapacitorPlugin annotation. */
export const APP_UPDATE_PLUGIN = 'AppUpdate';

interface AppUpdatePlugin {
  /** The installed build's own numbers, straight from the package manager. */
  current: () => Promise<{ versionCode: number; versionName: string }>;
  /** Downloads to the app's cache, verifying the digest. Rejects with a reason. */
  download: (options: { url: string; sha256?: string }) => Promise<{ path: string }>;
  /**
   * Hands the file to the system installer. `needsPermission` means the owner
   * has not allowed this app to install packages — the settings screen has just
   * been opened for them, and pressing the button again after it will work.
   */
  install: (options: { path: string }) => Promise<{ started: boolean; needsPermission: boolean }>;
  addListener: (
    event: 'updateProgress',
    handler: (progress: { loaded: number; total: number }) => void,
  ) => Promise<PluginListenerHandle>;
}

export const AppUpdate = registerPlugin<AppUpdatePlugin>(APP_UPDATE_PLUGIN);

interface CapacitorBridge {
  getPlatform?: () => string;
  isPluginAvailable?: (name: string) => boolean;
}

/** True only inside the Android shell, and only where the plugin really is. */
export const hasNativeUpdate: boolean = (() => {
  const capacitor = (globalThis as { Capacitor?: CapacitorBridge }).Capacitor;
  if (capacitor?.getPlatform?.() !== 'android') return false;
  return capacitor.isPluginAvailable?.(APP_UPDATE_PLUGIN) === true;
})();

/** Mirrors AndroidRelease in server/src/downloads.ts. */
export interface AndroidRelease {
  versionCode: number;
  versionName: string;
  file: string;
  size: number;
  sha256: string;
  builtAt: string;
  notes?: string;
}

/**
 * Validates a fetched manifest, or null.
 *
 * The server validates the same file before showing it on the download page,
 * and this checks it again on arrival — not out of distrust of the server but
 * because what a phone actually receives can be a captive portal's login page
 * with a 200 on it. The failure that matters is the quiet one: a manifest
 * missing `versionCode` compares as newer than anything and becomes a banner
 * that offers an update forever and installs nothing.
 */
export function parseRelease(value: unknown): AndroidRelease | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  if (typeof raw.versionCode !== 'number' || !Number.isFinite(raw.versionCode)) return null;
  if (typeof raw.versionName !== 'string' || raw.versionName === '') return null;
  if (typeof raw.file !== 'string' || raw.file === '') return null;
  if (typeof raw.size !== 'number' || raw.size <= 0) return null;
  if (typeof raw.sha256 !== 'string' || !/^[0-9a-f]{64}$/.test(raw.sha256)) return null;
  if (typeof raw.builtAt !== 'string') return null;
  return {
    versionCode: raw.versionCode,
    versionName: raw.versionName,
    file: raw.file,
    size: raw.size,
    sha256: raw.sha256,
    builtAt: raw.builtAt,
    ...(typeof raw.notes === 'string' && raw.notes !== '' ? { notes: raw.notes } : {}),
  };
}

/**
 * Whether this release should be offered, given what is installed and what was
 * already turned down.
 *
 * Strictly greater, because equal is the ordinary case — every launch of an
 * up-to-date app asks this — and because Android refuses to install an APK
 * whose `versionCode` is not above the installed one anyway, so offering it
 * would be offering a dialog that ends in «Приложение не установлено».
 *
 * A dismissal is remembered by version rather than by session: «не сейчас»
 * about one build should survive a relaunch, and should not silence the next
 * build. That is why `dismissed` is a number and not a flag.
 */
export function shouldOffer(
  release: AndroidRelease | null,
  installed: number | null,
  dismissed: number | null,
): boolean {
  if (release === null || installed === null) return false;
  if (release.versionCode <= installed) return false;
  return dismissed === null || release.versionCode > dismissed;
}

/** «10,7 МБ» — what the update is about to cost, in the banner. */
export function formatSize(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} МБ`;
}

const DISMISS_KEY = 'chat:update-dismissed';

export function dismissedVersion(): number | null {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (raw === null) return null;
    const code = Number(raw);
    return Number.isFinite(code) ? code : null;
  } catch {
    return null;
  }
}

export function rememberDismissed(versionCode: number): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(versionCode));
  } catch {
    // Storage unavailable. The banner comes back on the next launch, which is
    // the harmless direction to fail in.
  }
}
